
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Order, CartItem } from '../types';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

interface OrderContextType {
  orders: Order[];
  createOrder: (items: CartItem[], total: number, userEmail: string, userId?: string) => Promise<boolean>;
  isLoading: boolean;
  totalRevenue: number;
  totalOrders: number;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrders = async () => {
    setIsLoading(true);
    
    if (!isSupabaseConfigured) {
      // Keep state empty or use mocks if needed in dev without DB
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching orders:', error);
    } else if (data) {
      setOrders(data as Order[]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const createOrder = async (items: CartItem[], total: number, userEmail: string, userId?: string): Promise<boolean> => {
    // 1. Mock Mode Fallback
    if (!isSupabaseConfigured) {
      const newOrder: Order = {
        id: Math.random().toString(36).substr(2, 9),
        created_at: new Date().toISOString(),
        order_number: `NAT-${Math.floor(1000 + Math.random() * 9000)}`,
        user_email: userEmail,
        status: 'completed',
        platform: 'website',
        items: [...items],
        total
      };
      setOrders(prev => [newOrder, ...prev]);
      return true;
    }

    // 2. Real Database Insert
    const orderNumber = `NAT-${Math.floor(100000 + Math.random() * 900000)}`;
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .insert([{
          order_number: orderNumber,
          user_email: userEmail,
          user_id: userId || null,
          items: items, // Stored as JSONB
          total: total,
          status: 'pending',
          platform: 'website'
        }])
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setOrders(prev => [data as Order, ...prev]);
        
        // Optional: Simple stock reduction loop (Best practice is RPC or Backend Trigger)
        for (const item of items) {
           // Fire and forget stock update for smoother UX, or await if critical
           const { data: product } = await supabase.from('products').select('stock').eq('id', item.id).single();
           if (product && product.stock >= item.qty) {
             await supabase.from('products').update({ stock: product.stock - item.qty }).eq('id', item.id);
           }
        }
      }
      return true;

    } catch (err) {
      console.error("Order creation failed:", err);
      return false;
    }
  };

  const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
  const totalOrders = orders.length;

  return (
    <OrderContext.Provider value={{ orders, createOrder, isLoading, totalRevenue, totalOrders }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};
